'use client';
import * as S from './styles.ts';
import {
  H1,
  H3,
  IconBulletPoint,
  InfoBlock,
  P,
  UniversalLink,
} from '@template/components';
import { WebinarForm } from './Webinar';
import { useTypedSelector } from '@/redux/helpers/hooks.ts';
import { SupportForm } from './Support';
import { ResellerForm } from './Reseller';
import ArchStandaloneSectionComponent from '@template/blocks/ArchStandaloneSection/component';
import useBreakpoint from '@/lib/helpers/hooks/useBreakpoint.ts';
import { IInfoBlockProps } from '@template/components/InfoBlock';
import { ReviewsSummary } from '@template/blocks/ReviewsSummary';

interface IContactSectionProps {
  title?: string;
  description?: string;
  descriptionCtaText?: { label: string; href?: string }[];
  heading?: string;
  subHeading?: string;
  altBackground?: boolean;
  infoBullets?: {
    description: string;
    bullets: {
      text: string;
    }[];
  };
  infoBlocks?: boolean;
  context?: 'supportTicket' | 'webinar' | 'resellerEnquiry';
  reviews?: boolean;
}

const ContactSectionComponent = ({
  title,
  description,
  descriptionCtaText,
  infoBlocks,
  altBackground = false,
  heading,
  subHeading,
  context = 'supportTicket',
  reviews,
  infoBullets,
}: IContactSectionProps) => {
  const { check: isLg } = useBreakpoint('lg');
  const currency = useTypedSelector(
    (state) => state.store.site_version.currency
  );
  const isGBP = currency === 'GBP';

  const yayInfoBlocks: IInfoBlockProps[] = [
    {
      heading: 'Email',
      subHeading: 'Our friendly team is here to help.',
      iconName: 'Mail',
      color: 'primary',
      cta: {
        text: 'help@yay.com',
        link: 'mailto: help@yay.com',
        withIcon: false,
      },
    },
    {
      heading: 'Call us',
      subHeading: 'Mon – Friday, 7am to 10pm, Network Ops run 24/7',
      iconName: 'PhoneCall',
      color: 'primary',
      cta: {
        text: '0330 122 6000', // Update this with intl number isGBP ternary if applicable
        link: 'tel: 03301226000',
        withIcon: false,
      },
    },
    {
      heading: 'Visit us',
      subHeading: 'Yay.com Headquarters',
      iconName: 'MapPin',
      color: 'primary',
      cta: {
        text: isGBP
          ? 'Unit 4, Riverside Business Park Walnut Tree Close Guildford, GU1 4UG'
          : '26 Broadway, 8th Floor, New York, NY 10004, USA',
        link: 'https://www.google.com/maps/dir//Unit+4,+Riverside+Business+Park,+Walnut+Tree+Cl,+Guildford+GU1+4UG/@51.241815,-0.6623577,12z/data=!4m8!4m7!1m0!1m5!1m1!1s0x4875d0915c7d84ab:0xcdb88a3a6e150faa!2m2!1d-0.5799575!2d51.2418439?entry=ttu',
        withIcon: false,
        target: '_blank',
      },
    },
    {
      heading: '99.9% uptime',
      subHeading: 'We’re proud of our 99.9% uptime',
      iconName: 'Activity',
      color: 'primary',
      cta: {
        text: 'System Status',
        link: 'https://status.yay.com/?_gl=1*1nkl3gm*_ga*NDI3ODY2MjgzLjE3MTQ0NjkxMzM.*_ga_3QFM6NW4D1*MTcxNTY5MDA3NC40MS4xLjE3MTU2OTE2NjEuNjAuMC4xNDAzNzAwODI2',
        withIcon: false,
        target: '_blank',
      },
    },
  ];

  const hasCopy = title || description || descriptionCtaText || reviews;
  const hasInfoBullets = !!infoBullets;
  const altLayout = altBackground && !hasCopy && (infoBlocks || hasInfoBullets);

  return (
    <S.ContactSectionWrapper $altBackground={altBackground} id='contact-us'>
      <S.ContactSectionContainer>
        {hasCopy && (
          <S.ContactSectionContent>
            <S.ContactSectionHeader>Contact Us</S.ContactSectionHeader>
            {title && <H3>{title}</H3>}
            {description && <P>{description}</P>}
            {descriptionCtaText && (
              <P>
                {descriptionCtaText.map((segment, index) =>
                  segment.href ? (
                    <UniversalLink key={index} href={segment.href}>
                      {segment.label}
                    </UniversalLink>
                  ) : (
                    segment.label
                  )
                )}
              </P>
            )}

            {reviews && <ReviewsSummary />}
          </S.ContactSectionContent>
        )}

        {altLayout && (
          <>
            <ArchStandaloneSectionComponent
              color='white'
              background='dark'
              style={{
                position: 'absolute',
                left: 0,
                top: !isLg ? '23%' : '35%',
              }}
            />
            <S.LeftColumn>
              <S.HeadingContainer>
                <H1>{heading}</H1>
                <P>{subHeading}</P>
              </S.HeadingContainer>
              {infoBlocks && (
                <S.InfoBlocksContainer>
                  {yayInfoBlocks.map((info, index) => (
                    <InfoBlock
                      key={index}
                      heading={info.heading}
                      subHeading={info.subHeading}
                      iconName={info.iconName}
                      color={info.color}
                      cta={info.cta}
                      small={true}
                    />
                  ))}
                </S.InfoBlocksContainer>
              )}

              {hasInfoBullets && (
                <S.InfoBulletsContainer>
                  <P>{infoBullets.description}</P>
                  <S.InfoBullets>
                    {infoBullets.bullets.map((bullet, index) => (
                      <IconBulletPoint
                        key={index}
                        item={{
                          label: bullet.text,
                        }}
                      />
                    ))}
                  </S.InfoBullets>
                </S.InfoBulletsContainer>
              )}
            </S.LeftColumn>
          </>
        )}

        <S.ContactSectionForm>
          {context === 'webinar' && <WebinarForm />}
          {context === 'supportTicket' && <SupportForm />}
          {context === 'resellerEnquiry' && <ResellerForm />}
        </S.ContactSectionForm>
      </S.ContactSectionContainer>
    </S.ContactSectionWrapper>
  );
};

export default ContactSectionComponent;

export const options = [
  {
    description: '',
    displayName: 'ContactSectionComponent',
    methods: [],
    props: {
      title: {
        required: false,
        flowType: { name: 'string' },
        description: '',
      },
      description: {
        required: false,
        flowType: { name: 'string' },
        description: '',
      },
      descriptionCtaText: {
        required: false,
        flowType: {
          name: 'Array',
          elements: [
            {
              name: 'signature',
              type: 'object',
              raw: '{ label: string; href?: string }',
              signature: {
                properties: [
                  {
                    key: 'label',
                    value: {
                      name: 'string',
                      required: true,
                    },
                  },
                  {
                    key: 'href',
                    value: {
                      name: 'string',
                      required: false,
                    },
                  },
                ],
              },
            },
          ],
          raw: '{ label: string; href?: string }[]',
        },
        description: '',
      },
      heading: {
        required: false,
        flowType: { name: 'string' },
        description: '',
      },
      subHeading: {
        required: false,
        flowType: { name: 'string' },
        description: '',
      },
      altBackground: {
        required: false,
        flowType: { name: 'boolean' },
        description: '',
        defaultValue: { value: 'false', computed: false },
      },
      infoBullets: {
        required: false,
        flowType: {
          name: 'signature',
          type: 'object',
          raw: '{\n  description: string;\n  bullets: string[];\n}',
          signature: {
            properties: [
              {
                key: 'description',
                value: {
                  name: 'string',
                  required: true,
                },
              },
              {
                key: 'bullets',
                value: {
                  name: 'Array',
                  elements: [{ name: 'string' }],
                  raw: 'string[]',
                  required: true,
                },
              },
            ],
          },
        },
        description: '',
      },
      infoBlocks: {
        required: false,
        flowType: { name: 'boolean' },
        description: '',
      },
      context: {
        required: false,
        flowType: {
          name: 'union',
          raw: '"supportTicket" | "webinar" | "resellerEnquiry"',
          elements: [
            {
              name: 'literal',
              value: '"supportTicket"',
            },
            { name: 'literal', value: '"webinar"' },
            {
              name: 'literal',
              value: '"resellerEnquiry"',
            },
          ],
        },
        description: '',
        defaultValue: {
          value: '"supportTicket"',
          computed: false,
        },
      },
      reviews: {
        required: false,
        flowType: { name: 'boolean' },
        description: '',
      },
    },
  },
];
